import { Component, inject } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateModule } from '@ngx-translate/core';

export interface MobileDockLauncherItem {
  id: number;
  title: string;
  iconName: string;
}

export interface MobileDockLauncherData {
  items: MobileDockLauncherItem[];
  titleKey: string;
  /** Módulo activo según URL / rail (highlight honesto). */
  activeId?: number | null;
}

@Component({
  selector: 'app-milinco-mobile-dock-launcher',
  standalone: true,
  imports: [TranslateModule],
  template: `
    <div class="dock-launcher" role="dialog" [attr.aria-label]="data.titleKey | translate">
      <div class="dock-launcher__handle" aria-hidden="true"></div>
      <h2 class="dock-launcher__title">{{ data.titleKey | translate }}</h2>
      <div class="dock-launcher__grid">
        @for (item of data.items; track item.id) {
          <button
            type="button"
            class="dock-launcher__item"
            [class.dock-launcher__item--active]="item.id === data.activeId"
            (click)="pick(item.id)"
          >
            <span class="dock-launcher__icon iconify" [attr.data-icon]="item.iconName" aria-hidden="true"></span>
            <span class="dock-launcher__label">{{ item.title | translate }}</span>
          </button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        background: var(--mat-sys-surface, #fff);
        color: var(--mat-sys-on-surface, #1a1c1e);
      }

      .dock-launcher {
        padding: 0.5rem 1rem calc(1rem + env(safe-area-inset-bottom, 0px));
        background: var(--mat-sys-surface, #fff);
      }

      .dock-launcher__handle {
        width: 2.5rem;
        height: 0.25rem;
        margin: 0.25rem auto 0.75rem;
        border-radius: 999px;
        background: color-mix(in srgb, var(--mat-sys-on-surface) 22%, transparent);
      }

      .dock-launcher__title {
        margin: 0 0 0.85rem;
        font-size: 1rem;
        font-weight: 700;
        color: var(--mat-sys-on-surface);
      }

      .dock-launcher__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(4.75rem, 1fr));
        gap: 0.65rem;
      }

      .dock-launcher__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.35rem;
        min-height: 4.25rem;
        min-width: 44px;
        padding: 0.55rem 0.35rem;
        border: 1px solid color-mix(in srgb, var(--mat-sys-outline-variant) 70%, transparent);
        border-radius: 0.9rem;
        background: var(--mat-sys-surface-container-lowest, var(--mat-sys-surface, #fff));
        color: var(--mat-sys-on-surface);
        cursor: pointer;
        font: inherit;
      }

      .dock-launcher__item--active {
        border-color: transparent;
        background: color-mix(in srgb, var(--mat-sys-primary) 16%, var(--mat-sys-surface));
        color: var(--mat-sys-on-surface, #1a1c1e);
        box-shadow: inset 0 0 0 1.5px color-mix(in srgb, var(--mat-sys-primary) 55%, transparent);
      }

      .dock-launcher__item:hover,
      .dock-launcher__item:focus-visible {
        outline: none;
        border-color: color-mix(in srgb, var(--mat-sys-primary) 55%, transparent);
        background: color-mix(in srgb, var(--mat-sys-primary) 10%, var(--mat-sys-surface));
        color: var(--mat-sys-on-surface, #1a1c1e);
      }

      .dock-launcher__item--active:hover,
      .dock-launcher__item--active:focus-visible {
        background: color-mix(in srgb, var(--mat-sys-primary) 22%, var(--mat-sys-surface));
        color: var(--mat-sys-on-surface, #1a1c1e);
      }

      .dock-launcher__icon {
        width: 1.5rem;
        height: 1.5rem;
        font-size: 1.5rem;
        color: inherit;
      }

      .dock-launcher__label {
        font-size: 0.6875rem;
        font-weight: 600;
        line-height: 1.2;
        text-align: center;
      }
    `,
  ],
})
export class MilincoMobileDockLauncherComponent {
  readonly data = inject<MobileDockLauncherData>(MAT_BOTTOM_SHEET_DATA);
  private readonly sheetRef = inject(MatBottomSheetRef<MilincoMobileDockLauncherComponent, number | null>);

  pick(id: number): void {
    this.sheetRef.dismiss(id);
  }
}
